import { useEffect, useMemo, useState } from "react";
import { fetchNear } from "@/lib/api";
import { Loader2, MapPin, ArrowRight } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

const BOROUGHS = ["Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island"];
const BOROUGH_CENTROIDS = {
    Manhattan: [40.7831, -73.9712],
    Brooklyn: [40.6782, -73.9442],
    Queens: [40.7282, -73.7949],
    Bronx: [40.8448, -73.8648],
    "Staten Island": [40.5795, -74.1502],
};

/**
 * "Your next 15 classes" — primary above-the-fold experience.
 *
 * On mount we silently request the user's location. Once we have it (or the
 * user falls back to a borough), we hit /api/near with starts_soon_only and a
 * 10mi radius, then render the 15 nearest classes opening / running next.
 *
 * No filter UI here, no search bar, no map — this section answers the single
 * question every user actually has: "what's near me and starting soonest?"
 *
 * If location is denied, we show an inline borough picker so the experience
 * never reaches a dead-end. The full directory + filters live below this
 * section for users who want to browse.
 */
export function NextFifteen({ onPickClass, onBrowseAll }) {
    const [pos, setPos] = useState(null);
    const [posSource, setPosSource] = useState(null); // 'geo' | 'borough'
    const [boroughLabel, setBoroughLabel] = useState(null);
    const [geoState, setGeoState] = useState("idle"); // idle | requesting | denied | ok
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // Try silent geolocation on mount.
    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoState("denied");
            return;
        }
        setGeoState("requesting");
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setPos([p.coords.latitude, p.coords.longitude]);
                setPosSource("geo");
                setGeoState("ok");
            },
            () => setGeoState("denied"),
            { enableHighAccuracy: false, timeout: 12_000, maximumAge: 5 * 60_000 }
        );
    }, []);

    const pickBorough = (name) => {
        const c = BOROUGH_CENTROIDS[name];
        if (!c) return;
        setPos(c);
        setPosSource("borough");
        setBoroughLabel(name);
        setGeoState("ok");
    };

    // Fetch the 15 soonest near `pos` whenever it changes.
    useEffect(() => {
        if (!pos) return;
        let cancelled = false;
        setLoading(true);
        fetchNear({
            lat: pos[0],
            lng: pos[1],
            radius_miles: 10,
            starts_soon_only: true,
            include_live: false,
            limit: 15,
        })
            .then((r) => {
                if (!cancelled) setRows(r.slice(0, 15));
            })
            .catch(() => !cancelled && setRows([]))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [pos]);

    const headline = useMemo(() => {
        if (geoState !== "ok") return null;
        if (loading) return "Finding your next class…";
        if (!rows.length) return "Nothing within 10 miles right now.";
        const n = rows.length;
        return n === 1 ? "1 class next near you." : `${n} classes next near you.`;
    }, [geoState, loading, rows.length]);

    return (
        <section
            className="px-6 md:px-12 py-12 md:py-16 border-b border-border bg-background"
            data-testid="next-fifteen-section"
            id="next-fifteen"
        >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                    <p className="font-mono-label text-xs text-muted-foreground mb-3">
                        Find your <span className="text-primary">next class</span>
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        {geoState === "ok"
                            ? rows.length > 0
                                ? (
                                    <>
                                        Your <span className="font-editorial text-primary">next 15</span> —
                                        <br />
                                        starting <span className="font-editorial">soonest</span>.
                                    </>
                                )
                                : "Nothing nearby."
                            : geoState === "requesting"
                            ? (
                                <>
                                    Looking for the <span className="font-editorial text-primary">15 nearest</span>
                                    {" "}classes.
                                </>
                            )
                            : (
                                <>
                                    Find <span className="font-editorial text-primary">15</span> classes —
                                    starting <span className="font-editorial">soonest</span>.
                                </>
                            )}
                    </h2>
                    {geoState === "ok" && (
                        <p className="mt-3 font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                            {posSource === "geo"
                                ? "Within 10 miles of your location"
                                : `Within 10 miles of ${boroughLabel}`}
                        </p>
                    )}
                </div>
                <button
                    onClick={onBrowseAll}
                    className="self-start md:self-end px-4 py-2 font-mono-label text-[10px] uppercase tracking-widest border border-foreground bg-background hover-invert flex items-center gap-2"
                    data-testid="browse-all-cta"
                >
                    Browse all <ArrowRight className="h-3 w-3" />
                </button>
            </div>

            {/* States */}
            {geoState === "requesting" && (
                <div className="flex items-center gap-2 text-muted-foreground py-12" data-testid="next15-locating">
                    <Loader2 className="animate-spin h-4 w-4" />
                    <span className="text-sm">Locating you…</span>
                </div>
            )}

            {geoState === "denied" && (
                <div className="border border-dashed border-border p-6 md:p-8 bg-secondary" data-testid="next15-denied">
                    <p className="font-display text-2xl leading-tight">
                        We need your location <span className="font-editorial text-primary">or a borough</span>.
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                        Tap the lock icon in your address bar to allow location — or pick a
                        borough below and we'll center the next-15 on its core.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {BOROUGHS.map((b) => (
                            <button
                                key={b}
                                onClick={() => pickBorough(b)}
                                className="px-3 py-2 font-mono-label text-[10px] uppercase tracking-widest border border-foreground bg-background hover-invert"
                                data-testid={`next15-borough-${b.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {b}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {geoState === "ok" && loading && (
                <div className="flex items-center gap-2 text-muted-foreground py-12">
                    <Loader2 className="animate-spin h-4 w-4" />
                    <span className="text-sm">Pulling your 15…</span>
                </div>
            )}

            {geoState === "ok" && !loading && rows.length === 0 && (
                <div className="border border-dashed border-border p-6 bg-secondary">
                    <p className="font-display text-2xl leading-tight">
                        No classes opening in the next window within 10 miles.
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Try expanding to the full directory below — or pick another borough.
                    </p>
                </div>
            )}

            {geoState === "ok" && !loading && rows.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="next15-grid">
                    {rows.map((r, i) => (
                        <Next15Card key={r.id} row={r} rank={i + 1} onClick={() => onPickClass?.(r)} />
                    ))}
                </div>
            )}
        </section>
    );
}

function Next15Card({ row, rank, onClick }) {
    return (
        <button
            onClick={onClick}
            className="text-left bg-background hover:bg-secondary transition-colors p-5 flex flex-col h-full group"
            data-testid={`next15-card-${row.id}`}
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <span className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                    No. {String(rank).padStart(2, "0")} · {row.category}
                </span>
                {row.class_status && (
                    <StatusBadge status={row.class_status} size="compact" />
                )}
            </div>
            <h3 className="font-display text-2xl leading-tight group-hover:text-primary transition-colors">
                {row.class_name}
            </h3>
            <p className="font-editorial text-base text-muted-foreground mt-1">
                at {row.studio_name}
            </p>
            <div className="mt-auto pt-4 flex items-center gap-3 text-xs font-mono-label text-muted-foreground">
                <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {row.neighborhood || row.borough}
                </span>
                {row.distance_miles != null && (
                    <span>{row.distance_miles.toFixed(1)} mi</span>
                )}
                {row.duration_minutes && <span>{row.duration_minutes} min</span>}
                {row.price_per_class && <span>{row.price_per_class}</span>}
            </div>
        </button>
    );
}
